import { Product } from "./data";
import { getNumberOfDays, ONE_DAY_AS_MS } from "./utils";

export function calculatePrice(product: Product, count: number, fromDate?: Date, toDate?: Date) {
    if (product.itemPrice) {
        return [product.itemPrice!, count * product.itemPrice!];
    }

    if (!fromDate || !toDate) {
        // this should never happen
        return [product.dayPrice ?? 0, count * (product.dayPrice ?? 0)];
    }

    const { totaldays, weekdays, weekends } = computeDays(fromDate, toDate);
    let price = totaldays * count * (product?.dayPrice ?? 0);

    console.log("totaldays: " + totaldays + ", weekdays: " + weekdays + ", weekends: " + weekends + ", weekendprice: " + product.weekendPrice);

    // detect if there's a weekendprice that is relevant
    if (!!product.weekendPrice) {
        price = ((product.dayPrice ?? 0) * weekdays) + (product.weekendPrice * weekends);
        //console.log("using weekendprice");
    }

    // detect if there's a monthprice that is relevant
    if (!!product.monthPrice && totaldays < 31 && product.monthPrice < price) {
        price = product.monthPrice;
        //console.log("using monthprice");
    }

    return [price, count * price];
}

// Translated from the C# implementation
export function computeDays(fromDate: Date, toDate: Date) {
    // Create copies to avoid modifying the original dates
    const start = new Date(fromDate);
    let end = new Date(toDate);

    // Clear time component to work with full days
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    // Calculate total days in the range
    let totaldays = Math.max(1, Math.round((end.getTime() - start.getTime()) / ONE_DAY_AS_MS) + 1);
    let weekdays = totaldays;
    let weekends = 0;

    // DayOfWeek enum equivalent in TypeScript (0 = Sunday, 6 = Saturday)
    const Sunday = 0;
    const Saturday = 6;

    // Extend the period and extract the weekend, if the period ends on a weekend
    if (end.getDay() === Sunday) {
        weekends = 1;
        weekdays -= 2;
    } else if (end.getDay() === Saturday) {
        weekends = 1;
        weekdays -= 1;
        totaldays += 1;
        end = new Date(end.getTime() + ONE_DAY_AS_MS); // Add one day
    }

    weekdays = Math.max(0, weekdays);

    // Extract weekends that are inside the range (excluding the end weekend already handled above)
    {
        // Compute first weekend day (first Saturday) within range
        // Get days until first Saturday from starting date
        const daysToFirstSaturday = (Saturday - start.getDay() + 7) % 7;
        const firstWeekendDay = new Date(start.getTime() + daysToFirstSaturday * ONE_DAY_AS_MS);

        // Compute the last weekend day (last Sunday) within range,
        // excluding the end weekend already handled
        let lastToDateToConsider = new Date(end);
        if (end.getDay() === Saturday || end.getDay() === Sunday) {
            // If end date falls on weekend, we need to consider the previous Sunday
            const daysToSubtract = end.getDay() === Sunday ? 7 : 1;
            lastToDateToConsider = new Date(end.getTime() - daysToSubtract * ONE_DAY_AS_MS);
        }

        // Get days to go back to last Sunday from the adjusted end date
        const daysToLastSunday = (lastToDateToConsider.getDay() + 7 - Sunday) % 7;
        const lastWeekendDay = new Date(lastToDateToConsider.getTime() - daysToLastSunday * ONE_DAY_AS_MS);

        // Count additional weekends between firstWeekendDay and lastWeekendDay (inclusive),
        // but only if there are valid weekends in the range
        let additionalWeekends = 0;
        if (firstWeekendDay <= lastWeekendDay) {
            additionalWeekends = Math.floor((lastWeekendDay.getTime() - firstWeekendDay.getTime()) / (7 * ONE_DAY_AS_MS)) + 1;

            // Update the counts
            weekends += additionalWeekends;
            weekdays -= additionalWeekends * 2; // Each weekend has 2 days (Saturday and Sunday)
        }
    }

    return { totaldays, weekdays, weekends };
}